import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import ConsultingBreadcrumbs from "@/components/ConsultingBreadcrumbs";
import {
  ArrowLeft,
  Loader2,
  Briefcase,
  Target,
  ClipboardCheck,
  UserPlus,
  LineChart,
  MessageSquare,
  TrendingUp,
  Sparkles,
  Clock,
  FileText,
  RotateCw,
  Zap,
  Infinity as InfinityIcon,
  Award,
  History,
} from "lucide-react";

const ICONS = {
  briefcase: Briefcase,
  target: Target,
  "clipboard-check": ClipboardCheck,
  "user-plus": UserPlus,
  "line-chart": LineChart,
  "message-square": MessageSquare,
  "trending-up": TrendingUp,
};

const AREA_COLORS = {
  A: "#0F6E56",
  B: "#0891b2",
  C: "#7c3aed",
  D: "#ea580c",
  E: "#dc2626",
  F: "#0369a1",
  G: "#b45309",
};

// Metadatos concretos por herramienta (output + tiempo estimado)
const TOOL_META = {
  job_offer: { time: "~40 s", output: "Markdown editable · Copy-paste directo" },
  competency_profile: { time: "~50 s", output: "Markdown estructurado · PDF-listo" },
  interview_assistant: { time: "Guía interactiva", output: "Preguntas + scoring 1-3-5 + resumen" },
  onboarding_plan: { time: "~45 s", output: "Plan 30/60/90 · Responsables · Check-ins" },
  evaluation: { time: "~35 s", output: "Feedback listo para 1:1 · Markdown" },
  feedback: { time: "~30 s", output: "Feedback estructurado · Reutilizable" },
  development_plan: { time: "~50 s", output: "Objetivos, recursos y criterios de nivel" },
};

// Keywords para elegir la herramienta recomendada a partir de la 1ª prioridad del resumen ejecutivo.
const RECOMMEND_KEYWORDS = [
  { keys: ["oferta", "atracción", "employer branding", "reclutar", "reclutamiento"], tool: "job_offer" },
  { keys: ["perfil", "competencia", "competencias"], tool: "competency_profile" },
  { keys: ["entrevista", "candidato", "selección"], tool: "interview_assistant" },
  { keys: ["onboarding", "integración", "incorporación"], tool: "onboarding_plan" },
  { keys: ["evaluación", "desempeño", "performance"], tool: "evaluation" },
  { keys: ["feedback", "conversación"], tool: "feedback" },
  { keys: ["desarrollo", "carrera", "promoción", "crecimiento", "plan de desarrollo"], tool: "development_plan" },
];

function pickRecommendedTool(execSummary, tools) {
  if (!execSummary?.priorities?.length || !tools?.length) return tools?.[0]?.key || null;
  const text = (execSummary.priorities.join(" ") + " " + (execSummary.next_step || ""))
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
  for (const { keys, tool } of RECOMMEND_KEYWORDS) {
    for (const k of keys) {
      const norm = k
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "");
      if (text.includes(norm) && tools.some((t) => t.key === tool)) return tool;
    }
  }
  return tools[0]?.key || null;
}

function timeAgo(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const diffMs = Date.now() - d.getTime();
  const mins = Math.round(diffMs / 60000);
  if (mins < 1) return "hace un momento";
  if (mins < 60) return `hace ${mins} min`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `hace ${hours} h`;
  const days = Math.round(hours / 24);
  if (days === 1) return "ayer";
  if (days < 30) return `hace ${days} días`;
  const months = Math.round(days / 30);
  if (months < 12) return months === 1 ? "hace 1 mes" : `hace ${months} meses`;
  const years = Math.round(months / 12);
  return years === 1 ? "hace 1 año" : `hace ${years} años`;
}

function formatDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("es-ES", { day: "numeric", month: "long", year: "numeric" });
  } catch (_e) {
    return "";
  }
}

/**
 * Hub premium de las 7 herramientas AI de una consultoría.
 * Muestra: contexto de empresa + informe, propuesta de valor,
 * estado real por tarjeta, recomendación inteligente y últimas
 * generaciones.
 */
export default function ConsultingTools() {
  const { testId } = useParams();
  const navigate = useNavigate();
  const [tools, setTools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [test, setTest] = useState(null);
  const [execSummary, setExecSummary] = useState(null);
  const [historyMap, setHistoryMap] = useState({}); // { toolKey: { runs, last_run } }

  useEffect(() => {
    let cancelled = false;
    async function loadAll() {
      try {
        const [toolsRes, testRes] = await Promise.all([
          api.get("/consulting/tools"),
          api.get(`/consulting/tests/${testId}`).catch(() => null),
        ]);
        if (cancelled) return;
        const toolList = toolsRes.data.tools || [];
        setTools(toolList);
        setTest(testRes?.data || null);

        // Exec summary (opcional, para recomendación)
        api
          .get(`/consulting/tests/${testId}/executive_summary`)
          .then((r) => {
            if (!cancelled) setExecSummary(r.data);
          })
          .catch(() => {});

        // History por cada tool en paralelo
        const historyResults = await Promise.all(
          toolList.map((t) =>
            api
              .get(`/consulting/tests/${testId}/tools/${t.key}/history`)
              .then((r) => ({ key: t.key, items: r.data.items || [] }))
              .catch(() => ({ key: t.key, items: [] }))
          )
        );
        if (cancelled) return;
        const map = {};
        historyResults.forEach(({ key, items }) => {
          map[key] = { runs: items.length, items };
        });
        setHistoryMap(map);
      } catch (_e) {
        // silent
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    loadAll();
    return () => {
      cancelled = true;
    };
  }, [testId]);

  const recommendedKey = useMemo(() => pickRecommendedTool(execSummary, tools), [execSummary, tools]);

  // Últimas 3 generaciones (across all tools)
  const recentRuns = useMemo(() => {
    const all = [];
    tools.forEach((t) => {
      (historyMap[t.key]?.items || []).forEach((run) => {
        all.push({ ...run, tool: t.key, toolTitle: t.title, area: t.area });
      });
    });
    all.sort((a, b) => (b.updated_at || b.created_at || "").localeCompare(a.updated_at || a.created_at || ""));
    return all.slice(0, 3);
  }, [tools, historyMap]);

  return (
    <div
      className="min-h-screen py-8 md:py-12 px-4 md:px-6 overflow-x-hidden"
      style={{
        background:
          "radial-gradient(1200px 500px at 100% -10%, rgba(15,110,86,0.06) 0%, rgba(15,110,86,0) 60%)," +
          "radial-gradient(900px 400px at -10% 20%, rgba(201,168,76,0.06) 0%, rgba(201,168,76,0) 55%)," +
          "linear-gradient(180deg, #fafaf7 0%, #f5f5f1 100%)",
      }}
      data-testid="consulting-tools"
    >
      <div className="max-w-6xl mx-auto">
        <ConsultingBreadcrumbs
          crumbs={[
            { label: "Consultorías", to: "/consulting" },
            { label: test?.nickname || test?.company_name || "Consultoría", to: `/consulting/${testId}/report` },
            { label: "Recursos operativos" },
          ]}
        />
        <button
          onClick={() => navigate(`/consulting/${testId}/report`)}
          className="text-xs font-semibold text-slate-500 hover:text-[#0F6E56] inline-flex items-center gap-1.5 mb-6 md:mb-8 transition-colors group"
          data-testid="consulting-tools-back"
        >
          <ArrowLeft className="w-3.5 h-3.5 transition-transform group-hover:-translate-x-0.5" /> Volver al informe
        </button>

        {/* Hero premium */}
        <div
          className="relative rounded-3xl p-6 md:p-8 mb-8 md:mb-10 overflow-hidden"
          style={{
            background: "linear-gradient(135deg, #0b3d2e 0%, #14523f 55%, #0F6E56 100%)",
            boxShadow: "0 30px 60px -30px rgba(11,61,46,0.55), 0 0 0 1px rgba(201,168,76,0.15) inset",
          }}
          data-testid="consulting-tools-hero"
        >
          <span
            aria-hidden
            className="absolute -top-8 -right-8 pointer-events-none"
            style={{
              width: 240,
              height: 240,
              background:
                "radial-gradient(circle at 100% 0%, rgba(201,168,76,0.35) 0%, rgba(201,168,76,0) 65%)," +
                "repeating-linear-gradient(135deg, rgba(255,255,255,0.05) 0, rgba(255,255,255,0.05) 1px, transparent 1px, transparent 10px)",
            }}
          />
          <span
            aria-hidden
            className="absolute bottom-0 left-0 right-0 h-1"
            style={{ background: "linear-gradient(90deg, #C9A84C 0%, #E5C874 50%, #C9A84C 100%)" }}
          />
          <div className="relative flex items-start gap-5 flex-wrap">
            <div
              className="w-14 h-14 md:w-16 md:h-16 rounded-2xl flex items-center justify-center flex-shrink-0"
              style={{
                background: "linear-gradient(135deg, #C9A84C 0%, #E5C874 100%)",
                boxShadow: "0 8px 22px -6px rgba(201,168,76,0.55)",
              }}
            >
              <Sparkles className="w-7 h-7 md:w-8 md:h-8" style={{ color: "#0b3d2e" }} strokeWidth={2.25} />
            </div>
            <div className="min-w-0 flex-1">
              <div
                className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.28em] font-semibold mb-3 px-3 py-1 rounded-full"
                style={{ background: "rgba(201,168,76,0.15)", color: "#E5C874" }}
              >
                <span style={{ width: 6, height: 6, borderRadius: 999, background: "#C9A84C", display: "inline-block" }} />
                Set operativo
              </div>
              <h1 className="font-display text-3xl md:text-4xl font-bold text-white leading-tight" style={{ letterSpacing: "-0.02em" }}>
                7 productos adaptados{test?.company_name ? " a " : ""}
                {test?.company_name && (
                  <span
                    className="sm:whitespace-nowrap [overflow-wrap:anywhere]"
                    style={{
                      background: "linear-gradient(90deg, #E5C874 0%, #C9A84C 100%)",
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                      backgroundClip: "text",
                    }}
                  >
                    {test.company_name}
                  </span>
                )}
              </h1>
              <p className="text-sm mt-2 max-w-2xl" style={{ color: "rgba(255,255,255,0.78)" }}>
                Cada herramienta usa tu informe como contexto. Los outputs son específicos de tu empresa: cultura, EVP, perfiles y sistema de niveles definidos en tu diagnóstico.
              </p>
              {test?.report_ready_at && (
                <div
                  className="mt-4 pt-3 inline-flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px]"
                  style={{ borderTop: "1px solid rgba(255,255,255,0.12)", color: "rgba(255,255,255,0.55)" }}
                >
                  <span className="inline-flex items-center gap-1">
                    <FileText className="w-3 h-3" style={{ color: "#C9A84C" }} />
                    Basado en tu informe del {formatDate(test.report_ready_at)}
                  </span>
                  <span className="text-white/25">·</span>
                  <span>Consultoría OptimizaRRHH</span>
                </div>
              )}

              {/* Value strip: 3 pastillas de propuesta de valor */}
              <div className="mt-5 flex flex-wrap gap-2" data-testid="consulting-tools-value-strip">
                {[
                  { icon: Zap, label: "Ahorra 6-8 h por herramienta" },
                  { icon: Sparkles, label: "Personalizadas con tu diagnóstico" },
                  { icon: InfinityIcon, label: "Reutilizables sin coste" },
                ].map(({ icon: I, label }, i) => (
                  <div
                    key={i}
                    className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[11px] font-semibold"
                    style={{
                      background: "rgba(255,255,255,0.06)",
                      border: "1px solid rgba(201,168,76,0.25)",
                      color: "rgba(255,255,255,0.9)",
                      backdropFilter: "blur(6px)",
                    }}
                  >
                    <I className="w-3 h-3" style={{ color: "#E5C874" }} strokeWidth={2.25} />
                    {label}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Skeleton: 7 tarjetas placeholder */}
        {loading && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="consulting-tools-skeleton">
            {[0, 1, 2, 3, 4, 5, 6].map((i) => (
              <div
                key={i}
                className="rounded-[22px] p-7 bg-white"
                style={{ boxShadow: "0 22px 45px -30px rgba(11,61,46,0.15), 0 0 0 1px rgba(11,61,46,0.06)" }}
              >
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-14 h-14 rounded-2xl skeleton-pulse" />
                  <div className="skeleton-pulse h-3 w-8 rounded" />
                </div>
                <div className="skeleton-pulse h-4 w-3/4 rounded mb-3" />
                <div className="skeleton-pulse h-3 w-full rounded mb-2" />
                <div className="skeleton-pulse h-3 w-11/12 rounded mb-4" />
                <div className="skeleton-pulse h-6 w-24 rounded-full mt-4" />
              </div>
            ))}
          </div>
        )}

        {/* Grid de herramientas */}
        {!loading && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {tools.map((t, idx) => {
              const Icon = ICONS[t.icon] || Sparkles;
              const color = AREA_COLORS[t.area] || "#0F6E56";
              const meta = TOOL_META[t.key] || { time: "~1 min", output: "Markdown editable" };
              const hist = historyMap[t.key] || { runs: 0, items: [] };
              const isRecommended = recommendedKey === t.key;
              const lastRun = hist.items?.[0];
              return (
                <button
                  key={t.key}
                  onClick={() => navigate(`/consulting/${testId}/tools/${t.key}`)}
                  className="group text-left rounded-[22px] relative overflow-hidden transition-all duration-500 hover:-translate-y-1.5 tool-card-enter"
                  style={{
                    background: "linear-gradient(180deg, #ffffff 0%, #fdfcf6 100%)",
                    boxShadow: isRecommended
                      ? "0 26px 55px -28px rgba(201,168,76,0.55), 0 0 0 1.5px rgba(201,168,76,0.6), inset 0 1px 0 rgba(255,255,255,0.9)"
                      : "0 22px 45px -30px rgba(11,61,46,0.35), 0 0 0 1px rgba(11,61,46,0.06), inset 0 1px 0 rgba(255,255,255,0.9)",
                    animationDelay: `${idx * 70}ms`,
                  }}
                  data-testid={`consulting-tool-card-${t.key}`}
                >
                  {/* Franja vertical de color (spine) */}
                  <span
                    aria-hidden
                    className="absolute top-0 bottom-0 left-0 w-1 transition-all group-hover:w-1.5"
                    style={{ background: `linear-gradient(180deg, ${color} 0%, ${color}80 100%)` }}
                  />
                  {/* Halo dorado esquina superior derecha en hover */}
                  <span
                    aria-hidden
                    className="absolute -top-6 -right-6 w-24 h-24 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
                    style={{ background: `radial-gradient(circle at 100% 0%, ${color}22 0%, transparent 65%)` }}
                  />
                  {/* Barra dorada superior en hover */}
                  <span
                    aria-hidden
                    className="absolute top-0 left-1 right-0 h-[2px] opacity-0 group-hover:opacity-100 transition-opacity"
                    style={{ background: "linear-gradient(90deg, #C9A84C 0%, #E5C874 50%, #C9A84C 100%)" }}
                  />

                  {/* Badge "Recomendada" — flotante top-right */}
                  {isRecommended && (
                    <div
                      className="absolute top-3 right-3 inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.14em] z-10"
                      style={{
                        background: "linear-gradient(135deg, #C9A84C 0%, #E5C874 55%, #C9A84C 100%)",
                        color: "#0b3d2e",
                        boxShadow: "0 6px 16px -6px rgba(201,168,76,0.6)",
                      }}
                    >
                      <Award className="w-3 h-3" strokeWidth={2.5} />
                      Empezar aquí
                    </div>
                  )}

                  <div className="relative p-6 md:p-7">
                    {/* Header con icono + número + badge de área */}
                    <div className="flex items-start justify-between mb-5 gap-2">
                      <div className="flex items-center gap-3">
                        <div
                          className="w-12 h-12 md:w-14 md:h-14 rounded-2xl flex items-center justify-center transition-all duration-500 group-hover:scale-110 group-hover:rotate-3"
                          style={{
                            background: `linear-gradient(135deg, ${color} 0%, ${color}CC 100%)`,
                            boxShadow: `0 10px 24px -8px ${color}66, inset 0 -1px 0 rgba(255,255,255,0.2), inset 0 1px 0 rgba(255,255,255,0.35)`,
                          }}
                        >
                          <Icon className="w-5 h-5 md:w-6 md:h-6 text-white drop-shadow-sm" strokeWidth={2.25} />
                        </div>
                        <span
                          className="font-display text-[11px] font-bold uppercase tracking-[0.24em]"
                          style={{ color: "#94a3b8" }}
                        >
                          0{idx + 1}
                        </span>
                      </div>
                      {!isRecommended && (
                        <span
                          className="text-[10px] font-bold uppercase tracking-[0.18em] px-2.5 py-1 rounded-full whitespace-nowrap"
                          style={{
                            background: `${color}12`,
                            color,
                            border: `0.5px solid ${color}40`,
                          }}
                        >
                          Área {t.area}
                        </span>
                      )}
                    </div>

                    <h3
                      className="font-display font-bold text-slate-900 mb-2.5 leading-tight transition-colors group-hover:text-[color:var(--tool-color)]"
                      style={{ fontSize: "1.15rem", letterSpacing: "-0.015em", "--tool-color": color }}
                    >
                      {t.title}
                    </h3>
                    <p className="text-[13px] text-slate-500 leading-relaxed min-h-[48px]">{t.description}</p>

                    {/* Tira técnica: output + tiempo */}
                    <div
                      className="mt-4 flex flex-wrap items-center gap-x-2.5 gap-y-1 text-[11px] font-medium"
                      style={{ color: "#64748b" }}
                    >
                      <span className="inline-flex items-center gap-1">
                        <Clock className="w-3 h-3" style={{ color }} strokeWidth={2.25} />
                        {meta.time}
                      </span>
                      <span className="text-slate-300">·</span>
                      <span className="inline-flex items-center gap-1">
                        <FileText className="w-3 h-3" style={{ color }} strokeWidth={2.25} />
                        {meta.output}
                      </span>
                    </div>

                    {/* Estado real + CTA */}
                    <div className="mt-4 pt-4 flex items-center justify-between gap-3" style={{ borderTop: "1px dashed #eae7d8" }}>
                      <div className="flex items-center gap-1.5 text-[11px] min-w-0">
                        {hist.runs === 0 ? (
                          <>
                            <span
                              className="inline-block w-1.5 h-1.5 rounded-full flex-shrink-0"
                              style={{ background: "#cbd5e1" }}
                            />
                            <span className="text-slate-400 truncate">Sin usar todavía</span>
                          </>
                        ) : (
                          <>
                            <span
                              className="inline-block w-1.5 h-1.5 rounded-full flex-shrink-0"
                              style={{ background: color, boxShadow: `0 0 0 3px ${color}22` }}
                            />
                            <span className="text-slate-600 whitespace-nowrap font-semibold">
                              {hist.runs} generación{hist.runs > 1 ? "es" : ""}
                            </span>
                            {lastRun && <span className="text-slate-400 hidden sm:inline truncate">· {timeAgo(lastRun.updated_at || lastRun.created_at)}</span>}
                          </>
                        )}
                      </div>
                      <div
                        className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-[11px] font-bold uppercase tracking-[0.14em] transition-all group-hover:gap-2 flex-shrink-0"
                        style={{
                          background: isRecommended
                            ? "linear-gradient(135deg, #0b3d2e 0%, #0F6E56 100%)"
                            : `linear-gradient(135deg, ${color}12 0%, ${color}05 100%)`,
                          color: isRecommended ? "#E5C874" : color,
                          border: isRecommended ? "0.5px solid rgba(201,168,76,0.55)" : `0.5px solid ${color}22`,
                        }}
                      >
                        <span>{hist.runs > 0 ? "Reutilizar" : "Abrir"}</span>
                        <span className="transition-transform duration-300 group-hover:translate-x-0.5">→</span>
                      </div>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        )}

        {/* Generaciones recientes */}
        {!loading && recentRuns.length > 0 && (
          <div className="mt-10 md:mt-12" data-testid="consulting-tools-recent-runs">
            <div className="flex items-center gap-3 mb-5">
              <span style={{ width: 24, height: 1.5, background: "#C9A84C", display: "inline-block" }} />
              <div className="text-[10px] uppercase tracking-[0.28em] font-semibold" style={{ color: "#0b3d2e" }}>
                Generaciones recientes
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4">
              {recentRuns.map((run) => {
                const color = AREA_COLORS[run.area] || "#0F6E56";
                return (
                  <button
                    key={run.run_id}
                    onClick={() => navigate(`/consulting/${testId}/tools/${run.tool}?run=${run.run_id}`)}
                    className="group text-left rounded-2xl p-4 transition-all hover:-translate-y-0.5"
                    style={{
                      background: "#ffffff",
                      boxShadow: "0 10px 24px -18px rgba(11,61,46,0.28), 0 0 0 1px rgba(11,61,46,0.06)",
                    }}
                    data-testid={`consulting-tools-recent-${run.run_id}`}
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <History className="w-3 h-3" style={{ color }} strokeWidth={2.5} />
                      <span className="text-[10px] font-bold uppercase tracking-[0.18em]" style={{ color }}>
                        {run.toolTitle}
                      </span>
                    </div>
                    <div className="text-sm font-semibold text-slate-900 line-clamp-2 mb-1" title={run.title || run.inputs?.puesto || "Sin título"}>
                      {run.title || run.inputs?.puesto || "Sin título"}
                    </div>
                    <div className="text-[11px] text-slate-400">
                      {timeAgo(run.updated_at || run.created_at)}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
